import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useSelector } from "react-redux";
import { hasAnyPermission } from "../../lib/rbac";
import { CheckCircle2, Sparkles, X } from "lucide-react";
import Confetti from "react-confetti";
import {
  getContextualTourPageConfig,
  getFeaturesForPlatformStep,
  getPlatformStepIndexForPath,
  PATIENT_DETAIL_PATH,
  PLATFORM_TOUR_STEPS,
  resolvePatientDetailPath,
  resolvePlatformStepPath,
} from "../../lib/contextualTourPages";
import {
  contextualTourActions,
  useContextualTourStore,
} from "../../stores/contextualTourStore";
import FloatingTourAssistant from "./FloatingTourAssistant";

const TourContext = createContext(null);

const normalizePath = (path = "/") => path.split("?")[0].split("#")[0] || "/";

export function TourProvider({ children }) {
  const [location, navigate] = useLocation();
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const tour = useContextualTourStore((state) => state.tour);
  const path = normalizePath(location);
  const pageConfig = getContextualTourPageConfig(location);
  const platformStepIndex = tour.platformStepIndex || 0;
  
  const effectivePermissions = useSelector(
    (state) => state.me?.me?.effectivePermissions
  );

  const filteredPlatformSteps = useMemo(() => {
    return PLATFORM_TOUR_STEPS.filter(step => {
      if (!step.permissionChecks || step.permissionChecks.length === 0) return true;
      return hasAnyPermission(effectivePermissions, step.permissionChecks);
    });
  }, [effectivePermissions]);
  const tourFeatures = useMemo(
    () => getFeaturesForPlatformStep(platformStepIndex, path, pageConfig, filteredPlatformSteps),
    [platformStepIndex, path, pageConfig, filteredPlatformSteps]
  );
  const isPatientsUnifiedStep = Boolean(
    filteredPlatformSteps[platformStepIndex]?.unifiedFeatures
  );

  useEffect(() => {
    if (!tour.active) return;
    contextualTourActions.setRoute(path);
    const stepIndex = getPlatformStepIndexForPath(path, filteredPlatformSteps);
    if (stepIndex >= 0) {
      contextualTourActions.setPlatformStepIndex(stepIndex);
    }
  }, [path, tour.active, filteredPlatformSteps]);

  const startTour = useCallback(() => {
    if (filteredPlatformSteps.length === 0) return;
    const firstStep = filteredPlatformSteps[0];
    const firstPath = resolvePlatformStepPath(firstStep);
    contextualTourActions.startTour(firstPath, 0);
    if (path !== firstPath) {
      navigate(firstPath);
    }
  }, [navigate, path, filteredPlatformSteps]);

  // Auto-launch for new users
  useEffect(() => {
    if (effectivePermissions) {
      const hasSeenTour = localStorage.getItem("seismic:has-seen-tour");
      if (!hasSeenTour) {
        localStorage.setItem("seismic:has-seen-tour", "true");
        const timer = setTimeout(() => {
          startTour();
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [effectivePermissions, startTour]);

  const stopTour = useCallback(() => {
    contextualTourActions.stopTour();
  }, []);

  const skipTour = useCallback(() => {
    contextualTourActions.stopTour();
    navigate("/");
  }, [navigate]);

  const goBackPage = useCallback(() => {
    const currentIndex = tour.platformStepIndex || 0;
    if (currentIndex <= 0) return;
    const prevIndex = currentIndex - 1;
    const step = filteredPlatformSteps[prevIndex];
    if (step) {
      contextualTourActions.setPlatformStepIndex(prevIndex);
      contextualTourActions.setSelectedFeatureIndex(0);
      navigate(resolvePlatformStepPath(step));
    }
  }, [navigate, tour.platformStepIndex, filteredPlatformSteps]);

  const goNextPage = useCallback(() => {
    const currentIndex = tour.platformStepIndex || 0;
    if (currentIndex >= filteredPlatformSteps.length - 1) return;
    const nextIndex = currentIndex + 1;
    const step = filteredPlatformSteps[nextIndex];
    if (step) {
      contextualTourActions.setPlatformStepIndex(nextIndex);
      contextualTourActions.setSelectedFeatureIndex(0);
      navigate(resolvePlatformStepPath(step));
    }
  }, [navigate, tour.platformStepIndex, filteredPlatformSteps]);

  const goToFeatureIndex = useCallback(
    (index) => {
      if (index < 0 || index >= tourFeatures.length) return;
      contextualTourActions.setSelectedFeatureIndex(index);
    },
    [tourFeatures.length]
  );

  const goNextFocus = useCallback(() => {
    const current = tour.selectedFeatureIndex || 0;

    // 1. If not at the last feature of the current page, just increment feature index
    if (current < tourFeatures.length - 1) {
      contextualTourActions.setSelectedFeatureIndex(current + 1);
      return;
    }

    // 2. At the last feature of current page/view. Try to transition to the next page.
    if (isPatientsUnifiedStep && path === "/patients") {
      const detailPath = resolvePatientDetailPath();
      if (detailPath) {
        contextualTourActions.primeFeatureIndex(detailPath, 0);
        navigate(detailPath);
        return;
      }
    }

    const currentIndex = tour.platformStepIndex || 0;
    if (currentIndex < filteredPlatformSteps.length - 1) {
      const nextIndex = currentIndex + 1;
      const step = filteredPlatformSteps[nextIndex];
      contextualTourActions.setPlatformStepIndex(nextIndex);
      contextualTourActions.setSelectedFeatureIndex(0);
      navigate(resolvePlatformStepPath(step));
    } else {
      // Reached the very end of the tour, dispatch complete event
      window.dispatchEvent(new CustomEvent("tour-complete"));
      contextualTourActions.stopTour();
    }
  }, [
    isPatientsUnifiedStep,
    navigate,
    path,
    tour.selectedFeatureIndex,
    tour.platformStepIndex,
    tourFeatures.length,
    filteredPlatformSteps
  ]);

  const goBackFocus = useCallback(() => {
    const current = tour.selectedFeatureIndex || 0;

    // 1. If not at the first feature of the current page, just decrement feature index
    if (current > 0) {
      contextualTourActions.setSelectedFeatureIndex(current - 1);
      return;
    }

    // 2. At the first feature of current page/view. Try to transition to the previous page.
    if (isPatientsUnifiedStep && PATIENT_DETAIL_PATH.test(path)) {
      contextualTourActions.primeFeatureIndex("/patients", 0);
      navigate("/patients");
      return;
    }

    const currentIndex = tour.platformStepIndex || 0;
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      const step = filteredPlatformSteps[prevIndex];
      contextualTourActions.setPlatformStepIndex(prevIndex);
      contextualTourActions.setSelectedFeatureIndex(0);
      navigate(resolvePlatformStepPath(step));
    }
  }, [
    isPatientsUnifiedStep,
    navigate,
    path,
    tour.selectedFeatureIndex,
    tour.platformStepIndex,
    filteredPlatformSteps
  ]);

  const canGoBackPage = (tour.platformStepIndex || 0) > 0;
  const canGoNextPage =
    (tour.platformStepIndex || 0) < filteredPlatformSteps.length - 1;

  const canGoBackFocus = useMemo(() => {
    const current = tour.selectedFeatureIndex || 0;
    // We can go back if we are not at the very first step of the very first page
    if ((tour.platformStepIndex || 0) === 0 && current === 0) {
      return false;
    }
    return true;
  }, [tour.platformStepIndex, tour.selectedFeatureIndex]);

  const canGoNextFocus = useMemo(() => {
    // We can ALWAYS go "next", which will stop/complete the tour if at the very end
    return true;
  }, []);

  const displayPageConfig = useMemo(() => {
    if (isPatientsUnifiedStep) {
      return {
        ...pageConfig,
        title: "Patients",
        description:
          pagesConfigDescription(platformStepIndex, path, filteredPlatformSteps) || pageConfig.description,
        features: tourFeatures,
      };
    }
    return { ...pageConfig, features: tourFeatures };
  }, [isPatientsUnifiedStep, pageConfig, path, platformStepIndex, tourFeatures, filteredPlatformSteps]);

  const value = useMemo(
    () => ({
      tour,
      pageConfig: displayPageConfig,
      tourFeatures,
      currentRoute: location,
      platformSteps: filteredPlatformSteps,
      canGoBackPage,
      canGoNextPage,
      canGoBackFocus,
      canGoNextFocus,
      startTour,
      stopTour,
      skipTour,
      goBackPage,
      goNextPage,
      goBackFocus,
      goNextFocus,
      goToFeatureIndex,
      setMinimized: contextualTourActions.setMinimized,
      setSelectedFeatureIndex: contextualTourActions.setSelectedFeatureIndex,
      setPanelPosition: contextualTourActions.setPanelPosition,
      clearPanelPosition: () => contextualTourActions.setPanelPosition(null),
    }),
    [
      tour,
      displayPageConfig,
      tourFeatures,
      location,
      canGoBackPage,
      canGoNextPage,
      canGoBackFocus,
      canGoNextFocus,
      startTour,
      stopTour,
      skipTour,
      goBackPage,
      goNextPage,
      goBackFocus,
      goNextFocus,
      goToFeatureIndex,
      filteredPlatformSteps,
    ]
  );

  useEffect(() => {
    const onComplete = () => setShowCompleteModal(true);
    window.addEventListener("tour-complete", onComplete);
    return () => window.removeEventListener("tour-complete", onComplete);
  }, []);

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (!showCompleteModal) return;
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showCompleteModal]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <FloatingTourAssistant />
      {showCompleteModal && (
        <div className="fixed inset-0 z-[10005] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <div className="relative w-full max-w-sm rounded-xl border border-white/20 bg-white p-6 shadow-2xl animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowCompleteModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">You're all set!</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              You've completed the guided tour of Seismic Connect. You can always restart the tour later from the navigation if you need a refresher.
            </p>
            <button
              onClick={() => setShowCompleteModal(false)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 shadow-lg shadow-blue-600/20"
            >
              <Sparkles className="h-4 w-4" /> Let's get started
            </button>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
}

function pagesConfigDescription(platformStepIndex, location, platformSteps) {
  const path = normalizePath(location);
  if (
    platformSteps[platformStepIndex]?.unifiedFeatures &&
    PATIENT_DETAIL_PATH.test(path)
  ) {
    return "Review patient information, clinical summary, upcoming visits, and visit history.";
  }
  if (platformSteps[platformStepIndex]?.unifiedFeatures) {
    return "Access and manage patient records securely—search, filter, and open profiles for visit history.";
  }
  return null;
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within TourProvider");
  }
  return context;
}

export default TourProvider;
